import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Printer, Download, RefreshCw, RotateCcw, ShieldCheck, CheckCircle, Lock } from 'lucide-react';
import AddressForm from '../components/AddressForm';
import ParcelForm from '../components/ParcelForm';
import RateSelector from '../components/RateSelector';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { useBillableNavigationLock } from '../hooks/useBillableNavigationLock';
import { addressesApi, labelsApi, shipmentsApi, settingsApi } from '../api/client';
import { getRuntimeApiConfig } from '../api/config';
import { saveRecentOrder } from '../utils/recentOrders';
import { formatCurrency } from '../utils/currency';
import { buildPrintPath, labelExtension } from '../utils/labels';
import { normalizeShipmentMessages } from '../utils/shipmentMessages';
import {
  clearPreparedShipment,
  createPreparedShipment,
  loadPreparedShipment,
  savePreparedShipment,
} from '../utils/preparedShipment';
import type { PreparedShipmentPurchase } from '../utils/preparedShipment';
import { LEGACY_UNBOUND_API_IDENTITY, retryApiIdentityMatches } from '../utils/retryApiIdentity';
import type { Address, AddressRecord, Parcel, Rate, ShipmentPreview, ShipmentResult } from '../types';

const emptyAddress: Address = {
  name: '',
  company: '',
  street1: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
  email: '',
  phone: '',
  country: 'US',
};

const emptyParcel: Parcel = {};

function previewFromPrepared(prepared: PreparedShipmentPurchase): ShipmentPreview {
  return {
    needs_confirmation: true,
    shipment_id: prepared.previewShipmentId,
    rate: prepared.rate,
    to_address: prepared.normalizedToAddress,
    from_address: prepared.fromAddress,
    parcel: prepared.parcel,
    estimated_cost: prepared.rate.rate,
    messages: ['Recovered a prepared shipment from this browser session.'],
    rates: [prepared.rate],
    selected_rate_id: prepared.rate.id,
    insurance: prepared.insurance,
  };
}

function ShipmentPage() {
  const { toasts, removeToast, success, error } = useToast();
  const restoredPreparedRef = useRef<PreparedShipmentPurchase | null>(loadPreparedShipment());
  const restoredPrepared = restoredPreparedRef.current;

  // Form state
  const [toAddress, setToAddress] = useState<Address>(restoredPrepared?.toAddress ?? emptyAddress);
  const [fromAddress, setFromAddress] = useState<Address>(restoredPrepared?.fromAddress ?? emptyAddress);
  const [parcel, setParcel] = useState<Parcel>(restoredPrepared?.parcel ?? emptyParcel);
  const [savedAddresses, setSavedAddresses] = useState<AddressRecord[]>([]);

  // Preview/confirmation state
  const [preview, setPreview] = useState<ShipmentPreview | null>(
    restoredPrepared ? previewFromPrepared(restoredPrepared) : null
  );
  const [selectedRate, setSelectedRate] = useState<Rate | null>(restoredPrepared?.rate ?? null);
  const [preparedPurchase, setPreparedPurchase] = useState<PreparedShipmentPurchase | null>(restoredPrepared);
  const [showConfirmModal, setShowConfirmModal] = useState(Boolean(restoredPrepared));
  const [confirmChecked, setConfirmChecked] = useState(false);

  // Result state
  const [result, setResult] = useState<ShipmentResult | null>(null);

  // Loading states
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(!restoredPrepared);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState(false);
  const previewRequestRef = useRef(0);
  const confirmInFlightRef = useRef(false);
  const confirmAbortRef = useRef<AbortController | null>(null);

  useBillableNavigationLock(loadingConfirm);

  // Load saved addresses and settings on mount
  useEffect(() => {
    loadSavedAddresses();
    if (!restoredPreparedRef.current) loadSettings();
    return () => confirmAbortRef.current?.abort();
  }, []);

  const loadSavedAddresses = async () => {
    try {
      const addresses = await addressesApi.list();
      setSavedAddresses(addresses);
    } catch (err) {
      console.error('Failed to load saved addresses:', err);
      const message = err instanceof Error ? err.message : 'Failed to load saved addresses';
      error('Saved Addresses Unavailable', message);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const loadSettings = async () => {
    try {
      const settings = await settingsApi.get();
      if (settings.sender_address) {
        previewRequestRef.current += 1;
        setPreview(null);
        setSelectedRate(null);
        setLoadingPreview(false);
        setFromAddress({
          name: settings.sender_address.name,
          company: settings.sender_address.company,
          street1: settings.sender_address.street1,
          street2: settings.sender_address.street2,
          city: settings.sender_address.city,
          state: settings.sender_address.state,
          zip: settings.sender_address.zip,
          country: settings.sender_address.country,
          phone: settings.sender_address.phone,
          email: settings.sender_address.email,
        });
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
      const message = err instanceof Error ? err.message : 'Failed to load sender settings';
      error('Sender Settings Unavailable', message);
    } finally {
      setLoadingSettings(false);
    }
  };

  const invalidatePreview = () => {
    previewRequestRef.current += 1;
    setLoadingPreview(false);
    setPreview(null);
    setSelectedRate(null);
    setShowConfirmModal(false);
    setConfirmChecked(false);
  };

  const handleToAddressChange = (address: Address) => {
    if (preparedPurchase) return;
    setToAddress(address);
    if (preview || loadingPreview) invalidatePreview();
  };

  const handleParcelChange = (nextParcel: Parcel) => {
    if (preparedPurchase) return;
    setParcel(nextParcel);
    if (preview || loadingPreview) invalidatePreview();
  };

  const handlePreview = async (e: React.FormEvent) => {
    e.preventDefault();

    if (preparedPurchase) {
      setConfirmChecked(false);
      setShowConfirmModal(true);
      return;
    }

    // Validation
    if (!toAddress.name || !toAddress.street1 || !toAddress.city || !toAddress.state || !toAddress.zip) {
      error('Missing Address', 'Please fill in all required recipient address fields.');
      return;
    }

    if (loadingSettings) {
      error('Sender Still Loading', 'Wait for the sender settings to finish loading.');
      return;
    }

    if (!fromAddress.name || !fromAddress.street1 || !fromAddress.city || !fromAddress.state || !fromAddress.zip) {
      error('Missing Sender', 'Configure a complete sender address in Settings.');
      return;
    }

    if ((toAddress.country || 'US') !== (fromAddress.country || 'US')) {
      error(
        'Customs Information Required',
        'Cross-border shipments are not supported yet. The sender and recipient must be in the same country.'
      );
      return;
    }

    if (!parcel.weight || parcel.weight <= 0) {
      error('Missing Weight', 'Please enter the package weight.');
      return;
    }

    setLoadingPreview(true);
    setPreview(null);
    setSelectedRate(null);

    const requestId = ++previewRequestRef.current;

    try {
      const previewResult = await shipmentsApi.preview({
        to_address: toAddress,
        from_address: fromAddress,
        parcel,
      });

      if (requestId !== previewRequestRef.current) return;

      // Match every FedEx carrier variant (FedEx, FedExDefault — EasyPost's
      // default FedEx account, FedexSmartPost, …), not just an exact "FedEx".
      const fedExRates = (previewResult.rates || []).filter(
        (rate) => rate.carrier.toUpperCase().startsWith('FEDEX')
      );
      const filteredPreview = { ...previewResult, rates: fedExRates };
      setPreview(filteredPreview);

      // Auto-select first rate if available
      if (fedExRates.length > 0) {
        setSelectedRate(fedExRates[0]);
      }

      if (fedExRates.length === 0) {
        error('No FedEx Rates', 'No FedEx shipping options are available for this shipment.');
      } else {
        success('Rates Retrieved', `${fedExRates.length} FedEx shipping options available.`);
      }
    } catch (err) {
      if (requestId !== previewRequestRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to get shipping rates';
      error('Preview Failed', message);
    } finally {
      if (requestId === previewRequestRef.current) setLoadingPreview(false);
    }
  };

  const handleConfirmOpen = () => {
    if (preparedPurchase) {
      setConfirmChecked(false);
      setShowConfirmModal(true);
      return;
    }
    if (!selectedRate) {
      error('No Rate Selected', 'Please select a shipping rate before confirming.');
      return;
    }
    if (!preview) return;
    const apiConfig = getRuntimeApiConfig();
    if (!apiConfig) {
      error('API Connection Required', 'Reconnect to the Shipping Worker before preparing this purchase.');
      return;
    }
    const prepared = createPreparedShipment(preview, toAddress, fromAddress, parcel, selectedRate, apiConfig.apiUrl);
    if (!savePreparedShipment(prepared)) {
      error(
        'Purchase Recovery Unavailable',
        'Browser session storage is unavailable, so the prepared shipment cannot be retried safely.'
      );
      return;
    }
    setPreparedPurchase(prepared);
    setConfirmChecked(false);
    setShowConfirmModal(true);
  };

  const handleConfirmPurchase = async () => {
    const prepared = preparedPurchase;
    if (!prepared || confirmInFlightRef.current) return;

    const currentApiUrl = getRuntimeApiConfig()?.apiUrl || '';
    if (!retryApiIdentityMatches(prepared.apiUrl, currentApiUrl)) {
      error(
        prepared.apiUrl === LEGACY_UNBOUND_API_IDENTITY
          ? 'Legacy Retry Needs Its Original Worker'
          : 'Worker Connection Mismatch',
        prepared.apiUrl === LEGACY_UNBOUND_API_IDENTITY
          ? 'This older prepared shipment does not record which Worker created it. It has been preserved and will not be sent to an unverified Worker.'
          : `This prepared shipment belongs to ${prepared.apiUrl}. Reconnect to that Worker before retrying it.`
      );
      return;
    }

    confirmInFlightRef.current = true;
    setLoadingConfirm(true);
    const controller = new AbortController();
    confirmAbortRef.current = controller;
    const timeoutId = window.setTimeout(() => controller.abort(), 60000);

    try {
      const purchaseResult = await shipmentsApi.confirm({
        to_address: prepared.toAddress,
        from_address: prepared.fromAddress,
        parcel: prepared.parcel,
        confirm: true,
        confirmed_details: true,
        preview_shipment_id: prepared.previewShipmentId,
        rate_id: prepared.rate.id,
        accepted_rate_id: prepared.rate.id,
        accepted_rate_amount: prepared.rate.rate,
        accepted_rate_currency: prepared.rate.currency,
        ...(prepared.insurance
          ? {
              accepted_insurance_amount: prepared.insurance.amount,
              accepted_insurance_currency: prepared.insurance.currency,
            }
          : {}),
      }, controller.signal);

      clearPreparedShipment();
      setPreparedPurchase(null);
      setResult(purchaseResult);
      setShowConfirmModal(false);

      // Save to sessionStorage for immediate visibility on Order History page
      // This works around KV eventual consistency for list() operations
      saveRecentOrder(purchaseResult, prepared.toAddress, prepared.rate);

      success('Label Created!', 'Your shipping label has been purchased successfully.');
    } catch (err) {
      const message = controller.signal.aborted
        ? 'The purchase request timed out. The prepared shipment is still saved; retry it to recover the same purchase safely.'
        : err instanceof Error ? err.message : 'Failed to purchase label';
      error('Purchase Failed', message);
    } finally {
      window.clearTimeout(timeoutId);
      if (confirmAbortRef.current === controller) confirmAbortRef.current = null;
      confirmInFlightRef.current = false;
      setLoadingConfirm(false);
    }
  };

  const handleReset = () => {
    if (loadingConfirm) return;
    if (
      preparedPurchase &&
      !window.confirm(
        'Discard this prepared shipment? If a previous purchase response was interrupted, discarding its retry handle could lead to a duplicate label.'
      )
    ) {
      return;
    }
    previewRequestRef.current += 1;
    clearPreparedShipment();
    setPreparedPurchase(null);
    setToAddress(emptyAddress);
    setParcel(emptyParcel);
    setPreview(null);
    setSelectedRate(null);
    setResult(null);
    setConfirmChecked(false);
    // The original sender is immutable only while the restored prepared
    // shipment exists. A restore skipped the mount-time loadSettings and
    // seeded the sender from the snapshot, so reload Settings here — the next
    // shipment must not silently use stale sender details.
    if (restoredPreparedRef.current) {
      restoredPreparedRef.current = null;
      setFromAddress(emptyAddress);
      setLoadingSettings(true);
      void loadSettings();
    }
  };

  const formatAddress = (addr: Address) => {
    const parts = [addr.name];
    if (addr.company) parts.push(addr.company);
    parts.push(addr.street1);
    if (addr.street2) parts.push(addr.street2);
    parts.push(`${addr.city}, ${addr.state} ${addr.zip}`);
    if (addr.country && addr.country !== 'US') parts.push(addr.country);
    return parts;
  };

  const confirmationAddress: Address = preparedPurchase?.normalizedToAddress ?? {
    ...toAddress,
    ...(preview?.to_address || {}),
    company: preview?.to_address?.company || toAddress.company,
  };

  const handleDownloadLabel = async () => {
    if (!result?.label_url || loadingLabel) return;
    setLoadingLabel(true);
    try {
      const { blob, contentType } = await labelsApi.fetch(result.label_url);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `shipping-label.${labelExtension(contentType, result.label_file_type)}`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to download the label';
      error('Label Download Failed', message);
    } finally {
      setLoadingLabel(false);
    }
  };

  // Show result view after successful purchase
  if (result) {
    const resultMessages = normalizeShipmentMessages(result.messages);
    return (
      <>
        <ToastContainer toasts={toasts} onClose={removeToast} />

        <div className="panel" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h2 style={{ color: 'var(--success-text)', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <CheckCircle size={28} />
            Label Created Successfully!
          </h2>

          <div style={{ display: 'grid', gap: '16px' }}>
            {result.tracking_code && (
              <div>
                <strong>Tracking Number:</strong>
                <div style={{ fontFamily: 'monospace', fontSize: '1.1em', marginTop: '4px' }}>
                  {result.tracking_code}
                </div>
              </div>
            )}

            {result.rate && (
              <div>
                <strong>Service:</strong>
                <div style={{ marginTop: '4px' }}>
                  {result.rate.carrier} - {result.rate.service} ({formatCurrency(result.rate.rate, result.rate.currency)})
                </div>
              </div>
            )}

            {resultMessages.length > 0 && (
              <div
                style={{
                  background: 'var(--info-bg)',
                  padding: '12px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.9em',
                }}
              >
                {resultMessages.map((msg, i) => (
                  <div key={i}>{msg}</div>
                ))}
              </div>
            )}
          </div>

          <div className="action-buttons" style={{ marginTop: '24px' }}>
            {result.label_url && (
              <Link
                to={buildPrintPath(result.label_url, result.label_file_type)}
                className="btn btn-primary"
              >
                <Printer size={18} />
                Print Label
              </Link>
            )}
            {result.label_url && (
              <button
                type="button"
                onClick={handleDownloadLabel}
                disabled={loadingLabel}
                className="btn btn-secondary"
              >
                <Download size={18} />
                {loadingLabel ? 'Downloading...' : 'Download Label'}
              </button>
            )}
            <button className="btn btn-ghost" onClick={handleReset}>
              <RotateCcw size={18} />
              Create Another
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ToastContainer toasts={toasts} onClose={removeToast} />

      <PageHeader
        title="Create shipment"
        description="Domestic FedEx labels · rates fetched live from the Worker"
      />

      <form onSubmit={handlePreview}>
        <div className="ship-grid">
          <div className="ship-main">
            {preparedPurchase && (
              <div
                role="status"
                style={{
                  background: 'var(--info-bg)',
                  color: 'var(--info-text)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '11px 14px',
                  fontSize: '0.8rem',
                  lineHeight: 1.5,
                }}
              >
                {preparedPurchase.apiUrl === LEGACY_UNBOUND_API_IDENTITY
                  ? 'This legacy prepared shipment is preserved but blocked because it does not identify its original Worker. Do not discard it unless you have verified the earlier purchase state.'
                  : `A prepared shipment is saved for safe retry through ${preparedPurchase.apiUrl}. Its address, parcel, rate, and insurance terms are locked until it succeeds or you explicitly start over.`}
              </div>
            )}

            <div className="card">
              <div className="card-head">
                <span className="card-title">Recipient</span>
              </div>
              <div className="card-body">
                <AddressForm
                  title="Recipient Address"
                  address={toAddress}
                  onChange={handleToAddressChange}
                  savedAddresses={savedAddresses}
                  showSavedDropdown={!loadingAddresses}
                  readOnly={Boolean(preparedPurchase)}
                  hideHeader
                />
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <span className="card-title">Package</span>
              </div>
              <div className="card-body">
                <ParcelForm parcel={parcel} onChange={handleParcelChange} readOnly={Boolean(preparedPurchase)} hideHeader />
              </div>
            </div>
          </div>

          <div className="ship-rail">
            {/* Sender */}
            <div className="card">
              <div className="card-head">
                <span className="card-title">Sender</span>
                <span className="tag tag-neutral">
                  <Lock size={9} />
                  Locked
                </span>
              </div>
              <div className="sender-lines">
                {fromAddress.name ? (
                  <>
                    <div className="sender-name">{fromAddress.name}</div>
                    {fromAddress.company && <div>{fromAddress.company}</div>}
                    <div>
                      {fromAddress.street1}
                      {fromAddress.street2 ? `, ${fromAddress.street2}` : ''}
                    </div>
                    <div>
                      {fromAddress.city}, {fromAddress.state} {fromAddress.zip}
                    </div>
                    <div className="sender-edit">
                      <Link to="/settings">Edit in Settings</Link>
                    </div>
                  </>
                ) : (
                  <div style={{ color: 'var(--muted)' }}>
                    {loadingSettings ? (
                      'Loading sender…'
                    ) : (
                      <>
                        No sender configured. <Link to="/settings">Add one in Settings</Link>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* FedEx rates */}
            <div className="card">
              <div className="card-head">
                <span className="card-title">FedEx rates</span>
              </div>
              <div className="card-body">
                {loadingPreview ? (
                  <RateSelector rates={[]} selectedRateId={null} onSelect={() => {}} loading />
                ) : preview && preview.rates && preview.rates.length > 0 ? (
                  <RateSelector
                    rates={preview.rates}
                    selectedRateId={selectedRate?.id || null}
                    onSelect={(rate) => {
                      if (!preparedPurchase) setSelectedRate(rate);
                    }}
                    disabled={Boolean(preparedPurchase)}
                  />
                ) : (
                  <p style={{ fontSize: '0.76rem', color: 'var(--muted)', lineHeight: 1.5 }}>
                    Fetch rates to compare live FedEx services for this shipment.
                  </p>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="card">
              <div className="summary">
                {preview && (selectedRate || preparedPurchase?.rate) ? (
                  (() => {
                    const rate = (preparedPurchase?.rate ?? selectedRate)!;
                    const insurance = preparedPurchase?.insurance;
                    return (
                      <>
                        <div className="sum-row">
                          <span className="k">
                            {rate.carrier} {rate.service}
                          </span>
                          <span className="v">{formatCurrency(rate.rate, rate.currency)}</span>
                        </div>
                        <div className="sum-row">
                          <span className="k">Insurance</span>
                          <span className="v">
                            {insurance ? formatCurrency(insurance.amount, insurance.currency) : '—'}
                          </span>
                        </div>
                        <div className="total-row">
                          <span className="total-label">Total</span>
                          <span className="total-num">{formatCurrency(rate.rate, rate.currency)}</span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-accent btn-block"
                          onClick={handleConfirmOpen}
                          disabled={!selectedRate}
                        >
                          <ShieldCheck size={14} />
                          {preparedPurchase ? 'Resume purchase' : 'Purchase label'}
                        </button>
                        <button
                          type="submit"
                          className="btn btn-secondary btn-block"
                          style={{ marginTop: '8px' }}
                          disabled={loadingPreview || Boolean(preparedPurchase)}
                        >
                          <RefreshCw size={14} />
                          Refresh rates
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-block"
                          style={{ marginTop: '6px' }}
                          onClick={handleReset}
                        >
                          <RotateCcw size={14} />
                          Start over
                        </button>
                        <p className="summary-caption">Purchases are confirmed and retry-safe.</p>
                      </>
                    );
                  })()
                ) : (
                  <>
                    <p
                      style={{
                        fontSize: '0.76rem',
                        color: 'var(--muted)',
                        lineHeight: 1.5,
                        marginBottom: '12px',
                      }}
                    >
                      Enter a recipient and package, then fetch live FedEx rates to see the total.
                    </p>
                    <button
                      type="submit"
                      className={`btn btn-primary btn-block ${loadingPreview ? 'loading' : ''}`}
                      disabled={loadingPreview || loadingSettings}
                    >
                      <Zap size={16} />
                      {loadingSettings ? 'Loading sender…' : loadingPreview ? 'Getting rates…' : 'Get shipping rates'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => {
          if (!loadingConfirm) setShowConfirmModal(false);
        }}
        title="Confirm Purchase"
        dismissible={!loadingConfirm}
      >
        <p style={{ color: 'var(--aged-mahogany-dark)', marginBottom: '20px' }}>
          Please review the shipping details before purchasing your label.
        </p>

        <div
          style={{
            background: '#faf7f6',
            border: '1px solid var(--champagne-dune)',
            borderRadius: 'var(--radius-md)',
            padding: '20px',
            marginBottom: '20px',
          }}
        >
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <h4 style={{ color: 'var(--aged-mahogany-dark)', marginBottom: '8px' }}>
                Ship To:
              </h4>
              {formatAddress(confirmationAddress).map((line, i) => (
                <p key={i} style={{ margin: '2px 0', color: 'var(--copper-ember-dark)' }}>
                  {line}
                </p>
              ))}
            </div>

            <div>
              <h4 style={{ color: 'var(--aged-mahogany-dark)', marginBottom: '8px' }}>
                Package:
              </h4>
              <p style={{ color: 'var(--copper-ember-dark)' }}>
                {preparedPurchase?.parcel.weight ?? parcel.weight} oz
              </p>
            </div>
          </div>
        </div>

        {(preparedPurchase?.rate ?? selectedRate) && (
          <div
            style={{
              background: 'var(--desert-clay-light)',
              borderRadius: 'var(--radius-md)',
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
            }}
          >
            <div>
              <strong>{(preparedPurchase?.rate ?? selectedRate)!.carrier}</strong> - {(preparedPurchase?.rate ?? selectedRate)!.service}
            </div>
            <div style={{ fontWeight: 700, fontSize: '1.2em' }}>
              {formatCurrency((preparedPurchase?.rate ?? selectedRate)!.rate, (preparedPurchase?.rate ?? selectedRate)!.currency)}
            </div>
          </div>
        )}

        {preparedPurchase?.insurance && (
          <p style={{ marginBottom: '20px', color: 'var(--aged-mahogany-dark)' }}>
            <strong>Insured value:</strong>{' '}
            {formatCurrency(preparedPurchase.insurance.amount, preparedPurchase.insurance.currency)}.
            {' '}EasyPost charges an additional insurance fee of 1% of the insured value, with a $1.00 minimum.
          </p>
        )}

        <label className="checkbox-label" style={{ marginBottom: '20px' }}>
          <input
            type="checkbox"
            checked={confirmChecked}
            onChange={(e) => setConfirmChecked(e.target.checked)}
            disabled={loadingConfirm}
          />
          I have reviewed the shipping details and confirm this purchase
        </label>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            className="btn btn-ghost"
            onClick={() => setShowConfirmModal(false)}
            disabled={loadingConfirm}
          >
            Cancel
          </button>
          <button
            className={`btn btn-primary ${loadingConfirm ? 'loading' : ''}`}
            onClick={handleConfirmPurchase}
            disabled={!confirmChecked || loadingConfirm}
          >
            <ShieldCheck size={18} />
            {loadingConfirm ? 'Processing...' : 'Confirm & Purchase'}
          </button>
        </div>
      </Modal>
    </>
  );
}

export default ShipmentPage;
